import { supabase } from "@/integrations/supabase/client";

export interface ClientExploitation {
  id: string;
  client_id: string;
  description: string | null;
  product_category: string | null;
  month: number | null;
  sales: number;
  probability: number;
  year: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
}

export interface CreateExploitationData {
  client_id: string;
  description?: string;
  product_category?: string;
  month?: number;
  sales?: number;
  probability?: number;
  year: number;
}

export interface UpdateExploitationData {
  id: string;
  description?: string;
  product_category?: string;
  month?: number;
  sales?: number;
  probability?: number;
  year?: number;
}

export const clientExploitationService = {
  // Get exploitations for a client filtered by year
  getExploitations: async (clientId: string, year: number): Promise<ClientExploitation[]> => {
    const { data, error } = await supabase
      .from("client_exploitations")
      .select("*")
      .eq("client_id", clientId)
      .eq("year", year)
      .order("created_at", { ascending: true });

    if (error) throw error;
    return (data || []) as ClientExploitation[];
  },

  // Create new exploitation row
  createExploitation: async (exploitationData: CreateExploitationData): Promise<ClientExploitation> => {
    const { data: userData } = await supabase.auth.getUser();
    
    const { data, error } = await supabase
      .from("client_exploitations")
      .insert([{
        ...exploitationData,
        created_by: userData.user?.id
      }])
      .select()
      .single();

    if (error) throw error;
    return data as ClientExploitation;
  },

  // Update exploitation row
  updateExploitation: async ({ id, ...updateData }: UpdateExploitationData): Promise<ClientExploitation> => {
    const { data, error } = await supabase
      .from("client_exploitations")
      .update(updateData)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data as ClientExploitation;
  },

  // Delete exploitation row
  deleteExploitation: async (id: string): Promise<void> => {
    const { error } = await supabase
      .from("client_exploitations")
      .delete()
      .eq("id", id);

    if (error) throw error;
  },

  // Get available years for a client
  getAvailableYears: async (clientId: string): Promise<number[]> => {
    const { data, error } = await supabase
      .from("client_exploitations")
      .select("year")
      .eq("client_id", clientId);

    if (error) throw error;
    
    const years = [...new Set((data || []).map(d => d.year))];
    const currentYear = new Date().getFullYear();
    
    // Always include current year
    if (!years.includes(currentYear)) {
      years.push(currentYear);
    }
    
    return years.sort((a, b) => b - a);
  }
};
